import type { Selection } from '@aibi/contracts';
import { Badge, Button, cn, IconButton } from '@aibi/ui';
import { Eraser, X } from 'lucide-react';
import { useWorkspace, useWorkspaceActions } from '../../state/workspace-context';

const MAX_VALUES_SHOWN = 3;

const valueText = (values: Selection['values']): string => {
  const shown = values
    .slice(0, MAX_VALUES_SHOWN)
    .map((value) => (value === null ? '(vacio)' : String(value)));
  const rest = values.length - shown.length;
  return rest > 0 ? `${shown.join(', ')} +${String(rest)}` : shown.join(', ');
};

/**
 * Selecciones asociativas activas (de `AnalysisState.selections`), compartidas por las
 * visualizaciones con filtrado cruzado. `layout="bar"` es la barra superior del canvas.
 */
export function SelectionList({
  selections,
  layout = 'list',
}: {
  readonly selections: readonly Selection[];
  readonly layout?: 'list' | 'bar';
}) {
  const { catalog } = useWorkspace();
  const { execute } = useWorkspaceActions();

  if (selections.length === 0) {
    return layout === 'list' ? (
      <p className="px-2 py-1 text-xs text-muted-foreground">
        Haga clic en un valor de una visualizacion para seleccionarlo.
      </p>
    ) : null;
  }

  return (
    <div
      role="region"
      aria-label="Selecciones activas"
      className={cn(layout === 'bar' ? 'flex flex-wrap items-center gap-1.5' : 'space-y-1')}
    >
      {selections.map((selection) => {
        const name = catalog.get(selection.dimensionId)?.name ?? selection.dimensionId;
        return (
          <div
            key={selection.dimensionId}
            className="inline-flex max-w-full items-center gap-1 rounded-md border border-selection/40 bg-selection-muted py-0.5 pl-2 pr-0.5 text-xs"
          >
            <span className="font-medium text-selection">{name}:</span>
            <span className="truncate">{valueText(selection.values)}</span>
            <Badge variant="selection">{selection.values.length}</Badge>
            <IconButton
              label={`Quitar seleccion de ${name}`}
              className="size-5"
              onClick={() => {
                execute({ type: 'clear-selections', dimensionId: selection.dimensionId });
              }}
            >
              <X />
            </IconButton>
          </div>
        );
      })}
      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-2 text-xs"
        onClick={() => {
          execute({ type: 'clear-selections' });
        }}
      >
        <Eraser /> Limpiar selecciones
      </Button>
    </div>
  );
}
